/**
 * createPanelApp(config) → the whole admin/vendor app: providers, router, guards, layout.
 *
 * Route tree:
 *   <Root>                                        error boundary, providers
 *     auth pages (layout "auth")                  public; guestOnly pages bounce logged-in users
 *     <ProtectedArea>                             login required; loads /me, installed modules and
 *                                                 feature flags; maintenance page (vendor panel);
 *                                                 applies the panel guard (2FA, onboarding…)
 *       bare pages (layout "bare")                full screen
 *       <PanelLayout>                             sidebar + top bar
 *         panel pages (layout "panel")            module + permission + feature gated
 *         *                                       404
 */

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useIsMutating,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigation,
} from "react-router";

import { isApiError } from "@motolynk/api-client";
import { createI18n, I18nextProvider, useTranslation } from "@motolynk/i18n";
import { ErrorState, LoadingScreen, PageLoadBar, Toaster, TooltipProvider } from "@motolynk/ui";

import { AppConfigContext, useAppConfig, type PanelAppConfig } from "./config";
import { isFeatureOn, usePanelFeatures } from "./features";
import { PanelLayout } from "./layout";
import { validateModules, type ModuleDefinition, type ModuleRoute } from "./module";
import { TranslatedUiStrings } from "./ui-strings";
import {
  ForbiddenPage,
  MaintenancePage,
  NoPanelAccessPage,
  NotFoundPage,
  RouteErrorPage,
} from "./pages";
import {
  meKey,
  PanelContext,
  SESSION_KEY,
  useInstalledModules,
  usePanel,
  usePanelMe,
  usePermissions,
  useSession,
} from "./session";

export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (count, error) => !isApiError(error) && count < 2,
      },
    },
    // Any 401 means the session ended (logout elsewhere, expiry, block): re-check and go to login.
    // A 503 maintenance_mode means the platform just closed: reload the flags to show the page.
    queryCache: new QueryCache({
      onError: (error) => {
        if (isApiError(error) && error.status === 401)
          void client.invalidateQueries({ queryKey: SESSION_KEY });
        if (isApiError(error) && error.type === "maintenance_mode")
          void client.invalidateQueries({ queryKey: ["features"] });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        if (isApiError(error) && error.status === 401)
          void client.invalidateQueries({ queryKey: SESSION_KEY });
      },
    }),
  });
  return client;
}

// ── route guards ─────────────────────────────────────────────────────

function GuestOnly({ children }: { children: ReactNode }) {
  const session = useSession();
  const { homePath = "/" } = useAppConfig();
  const location = useLocation();
  if (session.isPending) return <LoadingScreen />;
  if (session.data?.authenticated) {
    const next = new URLSearchParams(location.search).get("next");
    return <Navigate to={isLocalPath(next) ? next : homePath} replace />;
  }
  return <>{children}</>;
}

function ProtectedArea() {
  const config = useAppConfig();
  const { t } = useTranslation();
  const location = useLocation();
  const session = useSession();
  const authenticated = Boolean(session.data?.authenticated);
  const me = usePanelMe(config.panel, authenticated);
  const installed = useInstalledModules(config.panel);
  const features = usePanelFeatures(config.panel, authenticated && me.isSuccess);
  const loginPath = config.loginPath ?? "/login";

  const preferredLanguage = me.data?.user.preferred_language;
  const { i18n } = useTranslation();
  useEffect(() => {
    if (preferredLanguage && preferredLanguage !== i18n.language)
      void i18n.changeLanguage(preferredLanguage);
  }, [preferredLanguage, i18n]);

  if (session.isPending) return <LoadingScreen label={t("app.loading")} />;
  if (!authenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`${loginPath}?next=${encodeURIComponent(next)}`} replace />;
  }
  if (me.isPending || installed.isPending) return <LoadingScreen label={t("app.loading")} />;
  if (me.error) {
    if (me.error.status === 403) return <NoPanelAccessPage message={me.error.message} />;
    return (
      <ErrorState
        title={t("errors.generic")}
        description={me.error.message}
        reference={me.error.requestId}
        referenceLabel={t("errors.reference")}
        retryLabel={t("actions.retry")}
        onRetry={() => void me.refetch()}
      />
    );
  }
  if (features.isPending) return <LoadingScreen label={t("app.loading")} />;
  // Flags couldn't be loaded: carry on with flag-guarded UI hidden (the API decides anyway).
  const featureValues = features.data ?? {};
  if (config.panel !== "admin" && featureValues["platform.maintenance_mode"] === true) {
    return <MaintenancePage />;
  }
  const redirect = config.guard?.(me.data, location.pathname);
  if (redirect && redirect !== location.pathname) return <Navigate to={redirect} replace />;

  return (
    <PanelContext.Provider
      value={{
        panel: config.panel,
        product: config.product,
        modules: config.modules,
        installed: new Set((installed.data ?? []).map((m) => m.name)),
        me: me.data,
        features: featureValues,
      }}
    >
      <Outlet />
    </PanelContext.Provider>
  );
}

/** Hides pages of modules the server doesn't have, and pages the user's role can't open. */
function ModuleGate({
  module,
  permission,
  feature,
  children,
}: {
  module: string;
  permission?: string;
  feature?: string;
  children: ReactNode;
}) {
  const { installed, features } = usePanel();
  const { can } = usePermissions();
  if (!installed.has(module) || !isFeatureOn(features, feature)) return <NotFoundPage />;
  if (!can(permission)) return <ForbiddenPage />;
  return <>{children}</>;
}

// ── activity feedback ────────────────────────────────────────────────

/**
 * One acknowledgement for everything that takes a moment: the top page-load bar and a "busy" cursor while the next
 * page loads (its code is fetched on demand) and while any change is being sent (save, submit, toggle, delete…), so
 * nobody wonders whether the click worked and clicks again. A mutation can opt out with `meta: { silent: true }`
 * (e.g. marking a notification read in the background).
 */
function ActivityIndicator() {
  const { t } = useTranslation();
  const navigating = useNavigation().state !== "idle";
  const saving = useIsMutating({ predicate: (mutation) => mutation.meta?.silent !== true }) > 0;
  const active = navigating || saving;
  useEffect(() => {
    document.documentElement.classList.toggle("cursor-progress", active);
  }, [active]);
  return <PageLoadBar active={active} tone="brand" label={t("app.loading")} />;
}

function RootFrame() {
  return (
    <>
      <ActivityIndicator />
      <Outlet />
    </>
  );
}

// ── router ───────────────────────────────────────────────────────────

function lazyPage(route: ModuleRoute, wrap: (Page: ComponentType) => ComponentType) {
  return async () => {
    const { default: Page } = await route.page();
    return { Component: wrap(Page) };
  };
}

export function buildRoutes(config: PanelAppConfig) {
  const entries = config.modules.flatMap((module: ModuleDefinition) =>
    (module[config.panel]?.routes ?? []).map((route) => ({ module: module.name, route })),
  );
  const byLayout = (layout: ModuleRoute["layout"]) =>
    entries.filter(({ route }) => (route.layout ?? "panel") === layout);

  const authRoutes = byLayout("auth").map(({ route }) => ({
    path: route.path,
    lazy: lazyPage(route, (Page) =>
      route.guestOnly
        ? function GuestPage() {
            return (
              <GuestOnly>
                <Page />
              </GuestOnly>
            );
          }
        : Page,
    ),
  }));
  const gated = (layout: "panel" | "bare") =>
    byLayout(layout).map(({ module, route }) => ({
      path: route.path,
      lazy: lazyPage(
        route,
        (Page) =>
          function GatedPage() {
            return (
              <ModuleGate module={module} permission={route.permission} feature={route.feature}>
                <Page />
              </ModuleGate>
            );
          },
      ),
    }));

  return [
    {
      element: <RootFrame />,
      errorElement: <RouteErrorPage />,
      children: [
        ...authRoutes,
        {
          element: <ProtectedArea />,
          children: [
            ...gated("bare"),
            {
              element: <PanelLayout />,
              errorElement: <RouteErrorPage />,
              children: [...gated("panel"), { path: "*", element: <NotFoundPage /> }],
            },
          ],
        },
      ],
    },
  ];
}

// ── app ──────────────────────────────────────────────────────────────

export function createPanelApp(config: PanelAppConfig): ComponentType {
  const problems = validateModules(config.modules);
  if (problems.length) throw new Error(`Invalid module list:\n${problems.join("\n")}`);

  return function PanelApp() {
    const [queryClient] = useState(createQueryClient);
    const i18n = useMemo(() => createI18n(config.modules.map((m) => namespaced(m))), []);
    const router = useMemo(() => createBrowserRouter(buildRoutes(config)), []);
    useEffect(() => {
      document.title = `MOTOLYNK ${config.product.charAt(0)}${config.product.slice(1).toLowerCase()}`;
    }, []);
    return (
      <AppConfigContext.Provider value={config}>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <TranslatedUiStrings namespace="common">
              <TooltipProvider>
                <RouterProvider router={router} />
                <Toaster />
              </TooltipProvider>
            </TranslatedUiStrings>
          </I18nextProvider>
        </QueryClientProvider>
      </AppConfigContext.Provider>
    );
  };
}

/** { en: {...} } of module "accounts" → { en: { accounts: {...} } } */
function namespaced(module: ModuleDefinition): Record<string, Record<string, unknown>> {
  const result: Record<string, Record<string, unknown>> = {};
  for (const [language, strings] of Object.entries(module.i18n ?? {})) {
    result[language] = { [module.name]: strings };
  }
  return result;
}

export { meKey };

/** `?next=` may only lead to a page of this panel: one leading slash, no backslashes or control characters (browsers
 * drop tabs/newlines, so "/\t/evil.com" would become "//evil.com", another site). */
export function isLocalPath(next: string | null | undefined): next is string {
  return !!next && next.startsWith("/") && !next.startsWith("//") && !/[\p{Cc}\\]/u.test(next);
}
